import React from 'react';
import { Plus, Trash2, Repeat, ChevronDown, ChevronRight } from '../Icons';
import { ProposalSection as IProposalSection, ProposalColors } from '../../types';

interface Props {
    section: IProposalSection;
    colors: ProposalColors;
    t: any;
    showCosts: boolean;
    updateSectionTitle: (id: number, title: string) => void;
    addService: (sectionId: number) => void;
    removeSection: (sectionId: number) => void;
    updateService: (sectionId: number, serviceId: number, field: string, value: any) => void;
    removeService: (sectionId: number, serviceId: number) => void;
    toggleServiceExpand: (sectionId: number, serviceId: number) => void;
    addDetail: (sectionId: number, serviceId: number) => void;
    removeDetail: (sectionId: number, serviceId: number, detailId: number) => void;
    updateDetail: (sectionId: number, serviceId: number, detailId: number, field: string, value: any) => void;
    addCost: (sectionId: number, serviceId: number) => void;
    removeCost: (sectionId: number, serviceId: number, costId: number) => void;
    updateCost: (sectionId: number, serviceId: number, costId: number, field: string, value: any) => void;
    calculations: any;
}

export const ProposalSection: React.FC<Props> = ({
    section, colors, t, showCosts,
    updateSectionTitle, addService, removeSection,
    updateService, removeService, toggleServiceExpand,
    addDetail, removeDetail, updateDetail,
    addCost, removeCost, updateCost,
    calculations
}) => {
    const { hexToRgba, calculateServiceTotal, calculateServiceCost, calculateServiceMargin, calculateServiceMarginPercent, calculateSectionTotal, calculateSectionCost, calculateSectionMargin } = calculations;

    return (
        <div className="bg-white rounded-lg shadow-lg mb-4 p-4 print:shadow-none print:p-0 print:mb-2">
            <div className="flex justify-between items-center mb-3 pb-3 border-b-2" style={{ borderColor: colors.primary }}>
                <textarea
                    value={section.title}
                    onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                    className="text-xl font-bold bg-transparent border-none focus:outline-none print:p-0 resize-none w-full print:text-lg"
                    style={{ color: colors.textPrimary, minHeight: '2.5rem', overflow: 'hidden' }}
                    onInput={(e: any) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px' }}
                    rows={1}
                />
                <div className="flex gap-2 print:hidden">
                    <button onClick={() => addService(section.id)} className="flex items-center gap-2 px-3 py-2 text-white rounded-lg text-sm" style={{ backgroundColor: colors.success }}>
                        <Plus /> {t.addService}
                    </button>
                    <button onClick={() => removeSection(section.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 />
                    </button>
                </div>
            </div>

            {/* PRINT HEADER */}
            {section.services.length > 0 && (
                <div className="hidden print:grid grid-cols-12 gap-4 items-center mb-2 pb-2 border-b-2" style={{ borderColor: hexToRgba(colors.textPrimary, 0.2) }}>
                    <div className="col-span-7 text-xs font-bold uppercase" style={{ color: colors.textSecondary }}>{t.serviceDescription}</div>
                    <div className="col-span-2 text-xs font-bold uppercase text-right" style={{ color: colors.textSecondary }}>{t.unitPrice}</div>
                    <div className="col-span-1 text-xs font-bold uppercase text-center" style={{ color: colors.textSecondary }}>{t.quantity}</div>
                    <div className="col-span-2 text-xs font-bold uppercase text-right" style={{ color: colors.textSecondary }}>{t.total}</div>
                </div>
            )}

            {/* SERVICES */}
            {section.services.map((service) => (
                <div key={service.id} className="mb-3 border-2 border-gray-200 rounded-lg overflow-hidden print:border-none print:mb-2">
                    <div className="p-3 print:p-0 print:bg-transparent service-row" style={{ backgroundColor: hexToRgba(colors.textPrimary, 0.03) }}>
                        <div className="grid grid-cols-12 gap-4 items-center print:items-start">
                            <div className="col-span-5 print:col-span-7">
                                <label className="text-xs font-semibold mb-1 block print:hidden" style={{ color: colors.textSecondary }}>{t.serviceDescription}</label>
                                <textarea
                                    value={service.name}
                                    onChange={(e) => updateService(section.id, service.id, 'name', e.target.value)}
                                    className="w-full px-3 py-2 border rounded-md font-semibold print:hidden resize-none"
                                    style={{ color: colors.textPrimary, minHeight: '2.5rem', overflow: 'hidden' }}
                                    onInput={(e: any) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px' }}
                                    rows={1}
                                />
                                <div className="hidden print:block font-bold text-sm break-words whitespace-pre-wrap" style={{ color: colors.textPrimary }}>
                                    {service.name}
                                </div>

                                <div className="mt-2 print:hidden">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="checkbox" checked={service.isRecurring} onChange={(e) => updateService(section.id, service.id, 'isRecurring', e.target.checked)} />
                                        <Repeat /> {t.recurring}
                                    </label>
                                    {service.isRecurring && (
                                        <div className="flex items-center gap-2 mt-1">
                                            <input type="number" value={service.recurringMonths} onChange={(e) => updateService(section.id, service.id, 'recurringMonths', parseInt(e.target.value) || 1)} className="w-20 px-2 py-1 border rounded text-sm" />
                                            <span className="text-sm">{t.months}</span>
                                            <span className="text-sm text-gray-600">x {service.unitPrice.toFixed(2)} € = {(service.unitPrice * service.recurringMonths).toFixed(2)} €</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs font-semibold mb-1 block print:hidden" style={{ color: colors.textSecondary }}>{t.unitPrice}</label>
                                <input type="number" value={service.unitPrice} onChange={(e) => updateService(section.id, service.id, 'unitPrice', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 border rounded-md text-right print:hidden" disabled={service.details.length > 0} style={{ color: colors.textPrimary }} />
                                <div className="hidden print:block text-sm text-right" style={{ color: colors.textPrimary }}>{service.unitPrice}</div>
                            </div>
                            <div className="col-span-1">
                                <label className="text-xs font-semibold mb-1 block print:hidden" style={{ color: colors.textSecondary }}>{t.quantity}</label>
                                <input type="number" value={service.quantity} onChange={(e) => updateService(section.id, service.id, 'quantity', parseInt(e.target.value) || 1)} className="w-full px-3 py-2 border rounded-md text-center print:hidden" disabled={service.details.length > 0} style={{ color: colors.textPrimary }} />
                                <div className="hidden print:block text-sm text-center" style={{ color: colors.textPrimary }}>{service.quantity}</div>
                            </div>
                            <div className="col-span-2 print:col-span-2">
                                <label className="text-xs font-semibold mb-1 block print:hidden" style={{ color: colors.textSecondary }}>{t.total}</label>
                                <div className="text-xl font-bold px-3 py-2 rounded-md text-right print:p-0 print:text-right print:text-base print:bg-transparent print:font-semibold total-cell" style={{ color: colors.primary, backgroundColor: hexToRgba(colors.primary, 0.1) }}>
                                    {calculateServiceTotal(service).toFixed(2)} €
                                </div>
                            </div>
                            <div className="col-span-2 flex gap-2 items-end justify-end print:hidden">
                                <button onClick={() => toggleServiceExpand(section.id, service.id)} className="p-2 hover:bg-gray-100 rounded-lg" style={{ color: colors.info }}>
                                    {service.expanded ? <ChevronDown /> : <ChevronRight />}
                                </button>
                                <button onClick={() => removeService(section.id, service.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                                    <Trash2 />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* DETAILS */}
                    {service.expanded && (
                        <div className="p-4 border-t-2 print:bg-transparent print:p-0 print:border-none details-section" style={{ backgroundColor: hexToRgba(colors.info, 0.1), borderColor: hexToRgba(colors.info, 0.3) }}>
                            <div className="flex justify-between items-center mb-3 print:hidden">
                                <h4 className="font-semibold" style={{ color: colors.info }}>{t.details}</h4>
                                <button onClick={() => addDetail(section.id, service.id)} className="flex items-center gap-1 px-3 py-1 text-white rounded-md text-sm" style={{ backgroundColor: colors.info }}>
                                    <Plus /> {t.addItem}
                                </button>
                            </div>
                            {service.details.map((detail) => (
                                <div key={detail.id} className="grid grid-cols-12 gap-3 mb-2 bg-white p-3 rounded-md print:p-0 print:mb-1 print:bg-white print:gap-4 items-start">
                                    <div className="col-span-6 print:col-span-7 print:pl-4">
                                        <textarea
                                            value={detail.name}
                                            onChange={(e) => updateDetail(section.id, service.id, detail.id, 'name', e.target.value)}
                                            className="w-full px-2 py-1 border rounded text-sm print:hidden resize-none overflow-hidden"
                                            style={{ minHeight: '2rem' }}
                                            onInput={(e: any) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px' }}
                                            rows={1}
                                        />
                                        <div className="hidden print:block text-sm break-words whitespace-pre-wrap" style={{ color: colors.textPrimary }}>
                                            {detail.name}
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <input type="number" value={detail.unitPrice} onChange={(e) => updateDetail(section.id, service.id, detail.id, 'unitPrice', parseFloat(e.target.value) || 0)} className="w-full px-2 py-1 border rounded text-right text-sm print:hidden" />
                                        <div className="hidden print:block text-sm text-right">{detail.unitPrice}</div>
                                    </div>
                                    <div className="col-span-1">
                                        <input type="number" value={detail.quantity} onChange={(e) => updateDetail(section.id, service.id, detail.id, 'quantity', parseInt(e.target.value) || 1)} className="w-full px-2 py-1 border rounded text-center text-sm print:hidden" />
                                        <div className="hidden print:block text-sm text-center">{detail.quantity}</div>
                                    </div>
                                    <div className="col-span-2 print:col-span-2">
                                        <div className="px-2 py-1 bg-gray-100 rounded text-right text-sm font-semibold print:bg-white print:p-0 print:text-right">
                                            {(detail.unitPrice * detail.quantity).toFixed(2)} €
                                        </div>
                                    </div>
                                    <div className="col-span-1 print:hidden">
                                        <button onClick={() => removeDetail(section.id, service.id, detail.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                                            <Trash2 />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* COSTS */}
                    {showCosts && (
                        <div className="p-4 border-t-2 print:hidden" style={{ backgroundColor: hexToRgba(colors.cost, 0.1), borderColor: hexToRgba(colors.cost, 0.3) }}>
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="font-semibold" style={{ color: colors.cost }}>{t.cost}</h4>
                                <button onClick={() => addCost(section.id, service.id)} className="flex items-center gap-1 px-3 py-1 text-white rounded-md text-sm" style={{ backgroundColor: colors.cost }}>
                                    <Plus /> {t.addCostItem}
                                </button>
                            </div>
                            {service.costs.map((cost) => (
                                <div key={cost.id} className="grid grid-cols-12 gap-3 mb-2 bg-white p-3 rounded-md items-start">
                                    <div className="col-span-6">
                                        <textarea
                                            value={cost.name}
                                            onChange={(e) => updateCost(section.id, service.id, cost.id, 'name', e.target.value)}
                                            className="w-full px-2 py-1 border rounded text-sm resize-none overflow-hidden"
                                            style={{ minHeight: '2rem' }}
                                            onInput={(e: any) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px' }}
                                            rows={1}
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <input type="number" value={cost.unitCost} onChange={(e) => updateCost(section.id, service.id, cost.id, 'unitCost', parseFloat(e.target.value) || 0)} className="w-full px-2 py-1 border rounded text-right text-sm" />
                                    </div>
                                    <div className="col-span-1">
                                        <input type="number" value={cost.quantity} onChange={(e) => updateCost(section.id, service.id, cost.id, 'quantity', parseInt(e.target.value) || 1)} className="w-full px-2 py-1 border rounded text-center text-sm" />
                                    </div>
                                    <div className="col-span-2">
                                        <div className="px-2 py-1 bg-gray-100 rounded text-right text-sm font-semibold">
                                            {(cost.unitCost * cost.quantity).toFixed(2)} €
                                        </div>
                                    </div>
                                    <div className="col-span-1">
                                        <button onClick={() => removeCost(section.id, service.id, cost.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                                            <Trash2 />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <div className="mt-3 pt-3 border-t" style={{ borderColor: hexToRgba(colors.cost, 0.4) }}>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="bg-white p-2 rounded">
                                        <span style={{ color: colors.textSecondary }}>{t.totalCost}</span>
                                        <span className="float-right font-bold" style={{ color: colors.cost }}>
                                            {calculateServiceCost(service).toFixed(2)} €
                                        </span>
                                    </div>
                                    <div className="p-2 rounded" style={{ backgroundColor: hexToRgba(colors.success, 0.2) }}>
                                        <span className="font-semibold" style={{ color: colors.textPrimary }}>{t.margin}</span>
                                        <span className="float-right font-bold" style={{ color: colors.success }}>
                                            {calculateServiceMargin(service).toFixed(2)} € ({calculateServiceMarginPercent(service)}%)
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ))}

            {section.services.length === 0 && (
                <div className="text-center py-12 print:hidden" style={{ color: colors.textSecondary }}>
                    <p className="mb-4">{t.noServices}</p>
                    <button onClick={() => addService(section.id)} className="px-4 py-2 text-white rounded-lg" style={{ backgroundColor: colors.success }}>
                        {t.addFirstService}
                    </button>
                </div>
            )}

            {section.services.length > 0 && (
                <div className="mt-4 pt-4 border-t-2 border-gray-300 print:border-t print:border-gray-200">
                    <div className="grid grid-cols-3 gap-4 print:flex print:justify-end">
                        <div className="p-3 rounded-lg print:p-0 print:bg-transparent print:rounded-none print:text-right section-total" style={{ backgroundColor: hexToRgba(colors.primary, 0.1) }}>
                            <div className="text-sm mb-1 print:text-xs" style={{ color: colors.textSecondary }}>{t.sectionTotal}</div>
                            <div className="text-xl font-bold print:text-base print:font-semibold" style={{ color: colors.primary }}>
                                {calculateSectionTotal(section).toFixed(2)} €
                            </div>
                        </div>
                        {showCosts && (
                            <>
                                <div className="p-3 rounded-lg print:hidden" style={{ backgroundColor: hexToRgba(colors.cost, 0.1) }}>
                                    <div className="text-sm mb-1" style={{ color: colors.textSecondary }}>{t.sectionCost}</div>
                                    <div className="text-2xl font-bold" style={{ color: colors.cost }}>
                                        {calculateSectionCost(section).toFixed(2)} €
                                    </div>
                                </div>
                                <div className="p-3 rounded-lg print:hidden" style={{ backgroundColor: hexToRgba(colors.success, 0.1) }}>
                                    <div className="text-sm mb-1" style={{ color: colors.textSecondary }}>{t.sectionMargin}</div>
                                    <div className="text-2xl font-bold" style={{ color: colors.success }}>
                                        {calculateSectionMargin(section).toFixed(2)} €
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
